/**
 * @fileoverview Approval queue filter bar (MOR-906)
 *
 * One filter surface over the submitted-entry queue: free-text search plus
 * person / matter / category / date-range.
 * `applyApprovalFilters` is the pure predicate `ApprovalsQueue` runs over the
 * loaded queue, so the filtering logic is unit-testable without mounting the
 * table (mirrors `src/calendar/components/header/task-filter-bar.tsx`).
 *
 * Filtering is client-side over whatever `entries.approvalQueue` returned —
 * see the cap note in ApprovalsQueue.
 */

import { Search, X } from "lucide-react";

import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  deriveFilterOptions,
  emptyFilters,
  isEmptyFilters,
  type ApprovalFilterState,
} from "./approvals-filter-bar.helpers";

export function ApprovalsFilterBar({
  filters,
  onChange,
  options,
}: {
  filters: ApprovalFilterState;
  onChange: (next: ApprovalFilterState) => void;
  options: ReturnType<typeof deriveFilterOptions>;
}) {
  const set = <K extends keyof ApprovalFilterState>(
    key: K,
    value: ApprovalFilterState[K],
  ) => onChange({ ...filters, [key]: value });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-background p-2 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <div className="relative min-w-56 flex-1">
        <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => set("search", e.target.value)}
          placeholder="Search person…"
          className="h-8 pl-8 text-xs"
          aria-label="Search entries"
        />
      </div>

      <MultiSearchableSelect
        value={filters.userIds}
        onValueChange={(v) => set("userIds", v)}
        options={options.people}
        placeholder="Person"
        searchPlaceholder="Search people…"
        className="h-8 w-40"
      />
      <MultiSearchableSelect
        value={filters.jobIds}
        onValueChange={(v) => set("jobIds", v)}
        options={options.matters}
        placeholder="Matter"
        searchPlaceholder="Search matters…"
        className="h-8 w-44"
      />
      <MultiSearchableSelect
        value={filters.categoryIds}
        onValueChange={(v) => set("categoryIds", v)}
        options={options.categories}
        placeholder="Category"
        searchPlaceholder="Search categories…"
        className="h-8 w-40"
      />

      <div className="flex items-center gap-1">
        <Label className="text-xs text-muted-foreground">Date</Label>
        <DatePicker
          value={filters.dateFrom}
          onChange={(value) => set("dateFrom", value)}
          placeholder="From"
          className="w-36"
          inputClassName="h-8 text-sm"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <DatePicker
          value={filters.dateTo}
          onChange={(value) => set("dateTo", value)}
          placeholder="To"
          className="w-36"
          inputClassName="h-8 text-sm"
        />
      </div>

      {!isEmptyFilters(filters) && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          onClick={() => onChange(emptyFilters())}
        >
          <X className="size-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
